<template>
  <InfuseTable
    :columns="columns"
    :editable="false"
    :filters="filters"
    :filter-configs="filterConfigs"
    :has-more-items="hasMoreItems"
    :loading="loading"
    :removable="false"
    :rows="rows"
    :search="search"
    :selectable="false"
    vertical-size="compact"
    @click-row="(row: AuditLog) => emit('clickRow', row)"
    @load="emit('load')"
    @add:filter="(f: Filter) => emit('add:filter', f)"
    @remove:filter="(f: Filter) => emit('remove:filter', f)"
    @search="(query: string) => emit('search', query)"
  >
    <template #createdAt="{ data }">
      <span>{{ formatDate(data) }}</span>
    </template>

    <template #action="{ data, row }">
      <InfuseTag v-if="data" :inline="true" variant="neutral" class="font-medium">
        <font-awesome-icon :icon="['fas', 'zap']" class="mr-2" />

        {{ data }}
      </InfuseTag>
      <InfuseTag v-else-if="row.httpMethod" :inline="true" variant="neutral" class="font-medium opacity-50">
        <font-awesome-icon :icon="['fal', 'globe']" class="mr-2" />

        {{ t(`httpMethod.${row.httpMethod}`) }}
      </InfuseTag>
      <span v-else>-</span>
    </template>

    <template #success="{ data }">
      <font-awesome-icon
        :icon="['fal', data ? 'circle-check' : 'circle-xmark']"
        class="h-5 w-5"
        :class="data ? 'text-success-500' : 'text-error-500'"
      />
    </template>
  </InfuseTable>
</template>

<script lang="ts" setup>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import type { AuditLog } from '@infuse/audit-log/store';
import { InfuseTable, InfuseTag } from '@infuse/ui/components';

import { type Filter, type FilterConfiguration, type UiTableColumn, UiTableColumnAlignment } from '@infuse/ui/domain';

import { formatDate } from '@infuse/ui/utils';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

interface Emits {
  (e: 'add:filter', filter: Filter): void;
  (e: 'clickRow', row: AuditLog): void;
  (e: 'load'): void;
  (e: 'remove:filter', filter: Filter): void;
  (e: 'search', query: string): void;
}

const emit = defineEmits<Emits>();

interface Props {
  hasMoreItems: boolean;
  loading?: boolean;
  page?: number;
  rows: AuditLog[];
  filters?: Filter[];
  filterConfigs?: FilterConfiguration[];
  searchEnabled?: boolean;
  searchQuery?: string;
  searchPlaceholder?: string;
  selectable?: boolean;
  showUserId?: boolean;
}

var props = withDefaults(defineProps<Props>(), {
  hasMoreItems: true,
  loading: false,
  rows: () => [],
  searchEnabled: true,
  searchQuery: '',
  selectable: false,
  showUserId: true
});

const { t } = useI18n();

const search = computed(() =>
  !props.searchEnabled
    ? undefined
    : {
        placeholder: t('auditLog.search.placeholder'),
        query: props.searchQuery
      }
);

const columns: UiTableColumn[] = [
  {
    name: 'createdAt',
    label: () => t('table.header.createdAt'),
    field: 'createdAt'
  },
  {
    name: 'userId',
    label: () => t('table.header.userId'),
    field: 'userId',
    bold: true
  },
  {
    name: 'objectType',
    label: () => t('table.header.objectType'),
    field: 'objectType'
  },
  {
    name: 'objectId',
    label: () => t('table.header.objectId'),
    field: 'objectId'
  },
  {
    name: 'action',
    label: () => t('table.header.action'),
    field: 'action'
  },
  {
    name: 'success',
    label: () => t('table.header.success'),
    field: 'success',
    align: UiTableColumnAlignment.CENTER
  }
].filter((column: UiTableColumn): boolean => props.showUserId || column.name !== 'userId');
</script>

<i18n>
{
  "en": {
    "table": {
      "header": {
        "createdAt": "Logged At",
        "userId": "User",
        "objectType": "Object type",
        "objectId": "Object ID",
        "action": "Action",
        "success": "Successful"
      }
    },
    "httpMethod": {
      "GET": "Access",
      "PUT": "Update",
      "POST": "Create",
      "DELETE": "Delete"
    }
  },
  "nl": {
    "table": {
      "header": {
        "createdAt": "Gelogd op",
        "userId": "Gebruiker",
        "objectType": "Objecttype",
        "objectId": "Object-ID",
        "action": "Actie",
        "success": "Succesvol"
      }
    },
    "httpMethod": {
      "GET": "Inzien",
      "PUT": "Aanpassen",
      "POST": "Creëren",
      "DELETE": "Verwijderen"
    }
  }
}
</i18n>
